'use client';

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { CountBadge, controlSizeClassName } from '@statura/ui';
import { Bell, CheckCheck } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useResolvedAuthUser } from '@/contexts/resolved-auth-user-context';
import { useAuthStore } from '@/stores/auth-store';
import { useTimezone } from '@/hooks/useTimezone';
import { useGovernmentIntegrations } from '@/contexts/government-integration-context';
import { filterGovernmentIntegrationNotices } from '@/lib/integrations/government/notice-visibility';
import type { GovernmentIntegrationEnablement } from '@/lib/integrations/government/enablement-types';
import {
  isNotificationUnread,
  isNotificationVisibleForProgram,
  toSafeInternalNotificationPath,
  type NotificationInboxRow,
  type NotificationProgramFamily,
} from '@/lib/notifications/inbox';
import {
  createNotificationInboxRefreshGuard,
  visibleNotificationInboxItems,
  type NotificationInboxSnapshot,
} from '@/lib/notifications/inbox-refresh-guard';

// ---- Types ------------------------------------------------------------

interface NotificationRecord extends NotificationInboxRow {
  title?: string | null;
  body?: string | null;
  type?: string;
  channel?: string;
  status: string | null;
  workflow_type?: string | null;
  notification_event_id?: string | null;
  sent_at?: string | null;
  acknowledged_at?: string | null;
  requires_acknowledgement?: boolean | null;
  is_mandatory?: boolean | null;
}

interface NotificationGroup {
  label: string;
  items: NotificationRecord[];
}

/** Assign each notification to a time-based group label.
 *  todayStr/yesterdayStr are YYYY-MM-DD strings in the tenant tz so a
 *  notification created at 23:55 AEST is correctly bucketed to "Today"
 *  even when the Vercel SSR runtime is in UTC.
 */
function getGroupLabel(
  dateStr: string,
  todayStr: string,
  yesterdayStr: string,
  tz: string,
): string {
  const dCalendarDay = new Date(dateStr).toLocaleDateString('en-CA', { timeZone: tz });
  if (dCalendarDay === todayStr) return 'Today';
  if (dCalendarDay === yesterdayStr) return 'Yesterday';
  if (dCalendarDay > todayStr) return 'Today'; // future-dated, treat as today
  return 'Earlier';
}

function groupNotifications(items: NotificationRecord[], tz: string): NotificationGroup[] {
  const map = new Map<string, NotificationRecord[]>();
  const order = ['Today', 'Yesterday', 'Earlier'];
  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: tz });
  const yesterdayDate = new Date(todayStr);
  yesterdayDate.setUTCDate(yesterdayDate.getUTCDate() - 1);
  const yesterdayStr = yesterdayDate.toLocaleDateString('en-CA', { timeZone: 'UTC' });
  for (const item of items) {
    const label = getGroupLabel(item.created_at, todayStr, yesterdayStr, tz);
    if (!map.has(label)) map.set(label, []);
    map.get(label)!.push(item);
  }
  return order
    .filter((label) => map.has(label))
    .map((label) => ({ label, items: map.get(label)! }));
}

function rpcProgramFamily(
  programFamily: NotificationProgramFamily,
): 'SAH' | 'RAC' | 'OTHER' | null {
  return programFamily === 'SAH' || programFamily === 'RAC' || programFamily === 'OTHER'
    ? programFamily
    : null;
}

/** PostgREST answers a call made without a live session as the public role. */
export function isSignedOutNotificationError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { code, status } = error as { code?: unknown; status?: unknown };
  return code === '42501' || code === 'PGRST301' || status === 401 || status === 403;
}

async function fetchUnreadCount(
  programFamily: NotificationProgramFamily,
  enabled: GovernmentIntegrationEnablement,
  role: string | null | undefined,
): Promise<number> {
  const sb = createClient() as any;
  const { data, error } = await sb.rpc('count_my_unread_notifications_controlled', {
    p_program_family: rpcProgramFamily(programFamily),
    p_enabled_government_integrations:
      role === 'OWNER' || role === 'TENANT_ADMIN'
        ? Object.entries(enabled)
            .filter(([, value]) => value)
            .map(([key]) => key)
        : [],
  });
  // The query only runs once the session is authenticated, so an EXECUTE
  // denial here is a grant regression and must stay visible to monitoring.
  if (error) throw error;
  return Number(data ?? 0);
}

async function fetchInboxNotifications(
  programFamily: NotificationProgramFamily,
  enabled: GovernmentIntegrationEnablement,
  role: string | null | undefined,
): Promise<NotificationRecord[]> {
  const sb = createClient() as any;
  const { data, error } = await sb.rpc('list_my_in_app_notifications_controlled', {
    p_limit: 20,
    p_program_family: rpcProgramFamily(programFamily),
    p_enabled_government_integrations:
      role === 'OWNER' || role === 'TENANT_ADMIN'
        ? Object.entries(enabled)
            .filter(([, value]) => value)
            .map(([key]) => key)
        : [],
  });
  if (error) throw error;
  return filterGovernmentIntegrationNotices(
    ((data ?? []) as NotificationRecord[])
      .map((row) => ({ ...row, status: row.status ?? null }))
      .filter((row) => isNotificationVisibleForProgram(row, programFamily)),
    role,
    enabled,
  );
}

// ---- Component --------------------------------------------------------

export function NotificationBell({
  programFamily,
  programContextRevision,
}: {
  programFamily: NotificationProgramFamily;
  programContextRevision: string;
}) {
  const user = useResolvedAuthUser();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const governmentIntegrations = useGovernmentIntegrations();
  const router = useRouter();
  const tz = useTimezone();
  const queryClient = useQueryClient();
  const inboxContextRevision = JSON.stringify({
    programContextRevision,
    userId: user?.id ?? null,
    role: user?.role ?? null,
    governmentIntegrations,
  });
  const currentInboxContext = {
    programContextRevision: inboxContextRevision,
    programFamily,
  } as const;
  const [inboxGuard] = useState(() => createNotificationInboxRefreshGuard(currentInboxContext));
  inboxGuard.syncContext(currentInboxContext);
  const [inboxSnapshot, setInboxSnapshot] = useState<NotificationInboxSnapshot<NotificationRecord>>(
    () => ({ ...currentInboxContext, items: [] }),
  );
  const notifications = filterGovernmentIntegrationNotices(
    visibleNotificationInboxItems(inboxSnapshot, currentInboxContext),
    user?.role,
    governmentIntegrations,
  );
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const openRef = useRef(false);

  // Unread badge count. PERF (2026-07-02 audit, Wave 3): moved onto react-query so
  // the ~37k/window count_my_unread_notifications_controlled calls collapse —
  // navigations within the provider's 30s staleTime reuse the cache instead of
  // refetching per mount. refetchOnWindowFocus is overridden true (the provider
  // default is false) so a STALE badge refreshes on focus; staleTime debounces
  // that to at most once/30s, and polling recovers changes made in another tab
  // without receiving row-bearing Realtime payloads.
  const unreadCountKey = [
    'notifications',
    'unread-count',
    user?.id ?? null,
    inboxContextRevision,
    programFamily,
    governmentIntegrations,
    user?.role,
  ] as const;
  const { data: unreadCount = 0 } = useQuery({
    queryKey: unreadCountKey,
    queryFn: () => fetchUnreadCount(programFamily, governmentIntegrations, user?.role),
    enabled: isAuthenticated && !!user?.id && !!user?.tenantId,
    refetchOnWindowFocus: true,
    // A tab whose session has ended keeps its store state for a while. Once the
    // count is refused as not signed in, stop polling rather than send a
    // refused call every 30 seconds; signing in again remounts the bell.
    refetchInterval: (query) => (isSignedOutNotificationError(query.state.error) ? false : 30_000),
    retry: (failureCount, error) => !isSignedOutNotificationError(error) && failureCount < 2,
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const refreshNotifications = useCallback(async () => {
    if (!user?.id) return;
    const ticket = inboxGuard.beginRefresh();
    try {
      const items = await fetchInboxNotifications(
        ticket.programFamily,
        governmentIntegrations,
        user?.role,
      );
      if (!inboxGuard.canCommit(ticket)) return;
      setInboxSnapshot({
        programContextRevision: ticket.programContextRevision,
        programFamily: ticket.programFamily,
        items,
      });
    } catch (err) {
      console.warn('[NotificationBell] inbox refresh failed:', err);
    }
  }, [governmentIntegrations, inboxGuard, user?.id, user?.role]);

  useEffect(() => {
    setInboxSnapshot({
      programContextRevision: inboxContextRevision,
      programFamily,
      items: [],
    });
  }, [inboxContextRevision, programFamily]);

  useEffect(() => {
    if (!user?.id) return;
    // The badge count refetches on focus via react-query (refetchOnWindowFocus,
    // debounced by the 30s staleTime). Keep refreshing the OPEN inbox on focus.
    const handleFocus = () => {
      if (openRef.current) void refreshNotifications();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refreshNotifications, user?.id]);

  // Fetch immediately when opened, then poll only through the programme-scoped
  // RPC. Direct row-change Realtime subscriptions are forbidden because their row
  // payload includes title/body/metadata before the RPC boundary can filter it.
  useEffect(() => {
    if (!open || !user?.id) return;
    void refreshNotifications();
    const interval = window.setInterval(() => void refreshNotifications(), 30_000);
    return () => window.clearInterval(interval);
  }, [open, refreshNotifications, user?.id]);

  const updateCurrentNotifications = useCallback(
    (update: (items: NotificationRecord[]) => NotificationRecord[]) => {
      setInboxSnapshot((previous) => {
        const currentItems = visibleNotificationInboxItems(previous, {
          programContextRevision: inboxContextRevision,
          programFamily,
        });
        return {
          programContextRevision: inboxContextRevision,
          programFamily,
          items: update(currentItems),
        };
      });
    },
    [inboxContextRevision, programFamily],
  );

  // Mark single notification as read and navigate
  const handleNotificationClick = useCallback(
    async (n: NotificationRecord) => {
      if (isNotificationUnread(n)) {
        const sb = createClient() as any;
        const { data, error } = await sb.rpc('mark_notification_read_controlled', {
          p_delivery_id: n.id,
        });
        if (error) throw error;
        const readAt = typeof data?.read_at === 'string' ? data.read_at : new Date().toISOString();
        updateCurrentNotifications((prev) =>
          prev.map((item) =>
            item.id === n.id ? { ...item, status: 'READ', read_at: readAt } : item,
          ),
        );
        void queryClient.invalidateQueries({ queryKey: unreadCountKey });
      }

      const url = toSafeInternalNotificationPath(n);
      if (url) {
        setOpen(false);
        router.push(url);
      }
    },
    [queryClient, router, unreadCountKey, updateCurrentNotifications],
  );

  // Mark all as read
  const handleMarkAllRead = useCallback(async () => {
    if (!user?.id) return;
    const visibleDeliveryIds = notifications.map((notification) => notification.id);
    if (visibleDeliveryIds.length === 0) return;
    const sb = createClient() as any;
    const { error } = await sb.rpc('mark_all_my_in_app_notifications_read_controlled', {
      p_delivery_ids: visibleDeliveryIds,
    });
    if (error) throw error;
    const readAt = new Date().toISOString();
    queryClient.setQueryData(unreadCountKey, 0);
    updateCurrentNotifications((prev) =>
      prev.map((n) => ({ ...n, status: 'READ', read_at: readAt })),
    );
    void refreshNotifications();
  }, [
    notifications,
    queryClient,
    refreshNotifications,
    unreadCountKey,
    updateCurrentNotifications,
    user?.id,
  ]);

  // Group notifications by date (in tenant tz, not Vercel-UTC)
  const grouped = useMemo(() => groupNotifications(notifications, tz), [notifications, tz]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Close on Escape key
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && open) setOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  return (
    <div ref={ref} className="relative no-print">
      <button
        onClick={() => setOpen(!open)}
        className={`${controlSizeClassName('toolbar')} relative inline-flex w-9 shrink-0 items-center justify-center rounded-control border border-control-border/30 !p-0 text-text-muted hover:bg-surface-soft hover:text-text-body focus:outline focus:outline-2 focus:outline-focus focus:-outline-offset-2 transition-colors`}
        aria-label="Notifications"
        aria-expanded={open}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <CountBadge
            count={unreadCount}
            label="unread notifications"
            className="absolute -right-1 -top-1 bg-status-danger-bg text-status-danger-fg"
          />
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-overlay border border-border bg-surface-sheet shadow-popover z-50">
          {/* Header with mark-all-read */}
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h3 className="text-body font-semibold text-text-heading">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="inline-flex items-center gap-1 text-caption text-statura-green-accessible hover:text-statura-green-accessible-hover transition-colors"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Mark all read
              </button>
            )}
          </div>

          {/* Grouped notification list */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <p className="px-4 py-8 text-center text-body text-text-muted">No notifications</p>
            ) : (
              grouped.map((group) => (
                <div key={group.label}>
                  {/* Group header */}
                  <div className="sticky top-0 bg-slate-50/90 px-4 py-1.5 backdrop-blur-sm">
                    <span className="text-eyebrow uppercase tracking-wider text-text-muted">
                      {group.label}
                    </span>
                  </div>
                  {group.items.map((n) => {
                    const hasLink = !!toSafeInternalNotificationPath(n);
                    const unread = isNotificationUnread(n);
                    return (
                      <button
                        key={n.id}
                        onClick={() => handleNotificationClick(n)}
                        className={`w-full px-4 py-3 text-left hover:bg-surface-soft border-b border-slate-50 transition-colors ${
                          unread ? 'bg-blue-50/50' : ''
                        }`}
                        aria-label={`${unread ? 'Unread: ' : ''}${n.title || n.type || 'Notification'}`}
                      >
                        <div className="flex items-start gap-2">
                          {unread && (
                            <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-statura-green-accessible" />
                          )}
                          <div className={unread ? 'min-w-0 flex-1' : 'min-w-0 flex-1 pl-4'}>
                            <p className="text-body text-text-heading line-clamp-2">
                              {n.title || n.type || 'Notification'}
                            </p>
                            {n.body && (
                              <p className="text-caption text-text-muted mt-0.5 line-clamp-1">
                                {n.body}
                              </p>
                            )}
                            <div className="mt-1 flex min-w-0 flex-wrap items-center gap-2">
                              <span className="text-caption text-text-muted">
                                {n.created_at
                                  ? new Date(n.created_at).toLocaleTimeString('en-AU', {
                                      timeZone: tz,
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })
                                  : ''}
                              </span>
                              {hasLink && (
                                <span className="text-caption text-statura-green-accessible">
                                  View
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
